import type { TmForm } from './tm-form';
import { TmFormControl } from './tm-form-control';
import { FormControlValidationResult } from './tm-form-control.types';

/**
 * Function invoked against each key:value pair a `TmForm` model to
 * build up a list of error messages.
 *
 * @param key The property name in the model.
 * @param value The property value corresponding to `key`.
 * @returns An array of error messages if the property is invalid, or nothing if valid.
 */
export type FormValidationBuilderCallback<T> = <K extends keyof T = keyof T>(
    key: K,
    value: T[K]
) => string[] | void;

/**
 * A function that invokes a callback against each key:value pair
 * in a form model to generate a validation result for each control in the form.
 *
 * Used optionally in conjunction with `TmFormValidator` to produce a `FormValidationResult`.
 *
 * @param callback The validation function to invoke against each key:value pair in the model.
 * @returns A list of validation results for each invalid control.
 */
type FormValidationBuilder<T> = (
    callback: FormValidationBuilderCallback<T>
) => FormValidationResult<T> | boolean;

/**
 * A function that validates the current state of a `TmForm` model.
 *
 * @param model The current state of the form expressed as key:value pairs where each key is
 * a form control name.
 *
 * @param builder A partially-applied helper function that can be used to
 * generate a line-item form validation result from the current model.
 */
export type TmFormValidator<T> = (
    model: Partial<T>,
    builder: FormValidationBuilder<T>
) => boolean | FormValidationResult<T>;

/**
 * A more fine-grained validator digest specified when there is a need
 * to provide additional UI context during form validation.
 */
export type FormValidationResult<T> = Record<keyof T, FormControlValidationResult<T[keyof T]>>;

export enum TmFormEvent {
    FormChanged = 'form-changed',
    FormSubmitted = 'form-submitted',
    FormReset = 'form-reset',
    FormClear = 'form-clear',
}

/**
 * A digest of errors for a specific control within a form.
 *
 * A `TmFormControl` instance specified by `control` implies that
 * the control is invalid whether or not it has any associated error messages.
 */
export interface ControlErrorDigest<T = unknown> {
    control: TmFormControl<T>;
    /**
     * Any messages describing the error state of this control.
     *
     * Can come from the control's own validation or from form-level validation.
     */
    errorMessages?: string[];
}

interface FormEventBase<T> {
    isPristine: boolean;
    model: Partial<T>;
}

export interface FormChangedPayload<T> extends FormEventBase<T> {
    isValid: boolean;
    errors: Record<keyof T, ControlErrorDigest<unknown>>;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FormSubmittedPayload<T> extends FormEventBase<T> {
    buttonName: string;
}

export type TmFormChanged<T> = CustomEvent<FormChangedPayload<T>>;
export type TmFormSubmitted<T> = CustomEvent<FormSubmittedPayload<T>>;

type FormEventPayload<T> = {
    [TmFormEvent.FormChanged]: FormChangedPayload<T>;
    [TmFormEvent.FormReset]: FormChangedPayload<T>;
    [TmFormEvent.FormClear]: FormChangedPayload<T>;
    [TmFormEvent.FormSubmitted]: FormSubmittedPayload<T>;
};

export function dispatchFormEvent<Event extends TmFormEvent, Model>(
    control: TmForm<Model>,
    event: Event,
    payload: FormEventPayload<Model>[Event]
) {
    const valueChangedEvent = new CustomEvent(event, {
        detail: payload,
    });

    return control.dispatchEvent(valueChangedEvent);
}
